import { ReactElement, useCallback, useEffect } from 'react';
import { TextField } from '@mui/material';
import { useFormik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { useSubscriptionFeatures, useLazyRequest } from '~hooks/index';
import { createItemPreset } from '~services/item';
import { ItemPreset, ItemPresetParse } from '~services/item/types';
import { OrdersListFilters } from '~hooks/useOrdersListFilters';
import { DialogConfimation } from '~components/index';
import { VALIDATIONS_ERROR } from '~constants/index';
import { hasError } from '~utils/formHelpers';
import { convertOrderListFilterToItemFilter } from '~utils/order';
import { camelCaseSerializer } from '~utils/serializer';
import { PlanFeaturesTypes } from '~globals/types/enums';

interface OrdersFiltersPresetsDialogCreateProps {
  open: boolean;
  onClose: () => void;
  currentFilters: OrdersListFilters;
  onSuccess: (currentPreset: ItemPresetParse) => void;
}

const FIELDS_NAME = {
  NAME: 'name',
} as const;

const initialValues = {
  [FIELDS_NAME.NAME]: '',
};

const validationSchema = Yup.object().shape({
  [FIELDS_NAME.NAME]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
});

const OrdersFiltersPresetsDialogCreate = ({
  open,
  onClose,
  currentFilters,
  onSuccess,
}: OrdersFiltersPresetsDialogCreateProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const [, loadingCreatePreset, , executeCreatePreset] = useLazyRequest({
    request: createItemPreset,
    withPostSuccess: (response) => {
      const presetResponse = response.data?.data as ItemPreset;
      const parsePresetResponse: ItemPresetParse = {
        ...presetResponse,
        data: camelCaseSerializer.serialize(JSON.parse(presetResponse.data)),
      };
      const message = `El filtro ${parsePresetResponse.description} fue creado correctamente`;

      onSuccess(parsePresetResponse);

      enqueueSnackbar(message, { variant: 'success' });
    },
    withPostFailure: () => {
      enqueueSnackbar('No se pudo crear el filtro, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const executeSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      const data = JSON.stringify(
        convertOrderListFilterToItemFilter(currentFilters, {
          limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
          mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
        }),
      );

      await executeCreatePreset({
        description: values[FIELDS_NAME.NAME],
        data,
      });

      setSubmitting(false);
    },
    [currentFilters, executeCreatePreset, hasAccessToFeature],
  );

  const {
    errors,
    touched,
    getFieldProps,
    submitForm,
    isSubmitting,
    dirty,
    isValid,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: executeSubmit,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogConfimation
      open={open}
      title="Crear filtro personalizado"
      description="Defina un nombre para su filtro personalizado"
      confirmText="Guardar"
      onConfirm={submitForm}
      onCancel={onClose}
      disabledConfirm={!dirty || !isValid}
      loadingConfirm={isSubmitting || loadingCreatePreset}
    >
      <TextField
        label="Nombre"
        fullWidth
        error={hasError(touched, errors, FIELDS_NAME.NAME)}
        {...getFieldProps(FIELDS_NAME.NAME)}
      />
    </DialogConfimation>
  );
};

export default OrdersFiltersPresetsDialogCreate;
